import { defineStore } from "pinia";
import { db } from "@/firebase";
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { createPrefixedLogger } from "@/utils/logger";

const logger = createPrefixedLogger("AREAS");

export const useAreasStore = defineStore("areas", {
  state: () => ({
    list: [],
    areasData: {}, // Store full area data including available maps
    currentArea: null,
    loaded: false,
    loading: false,
    error: null,
  }),

  actions: {
    async fetchAreas() {
      // Return cached list if already loaded
      if (this.loaded && this.list.length > 0) {
        logger.debug("Using cached areas list");
        return this.list;
      }

      // Prevent duplicate fetches
      if (this.loading) {
        logger.debug("Areas fetch already in progress");
        return this.list;
      }

      this.loading = true;
      this.error = null;

      try {
        logger.debug("Fetching areas from Firestore");
        const areasRef = collection(db, "areas");
        const snapshot = await getDocs(areasRef);

        this.list = [];
        this.areasData = {};

        snapshot.forEach((docSnap) => {
          const id = docSnap.id;
          const data = docSnap.data();
          this.list.push(id);
          this.areasData[id] = {
            ...data,
            id,
            // Default available maps if not specified in Firestore
            availableMaps: data.availableMaps || {
              ageb: true,
              electoral: true,
              indices: true,
              compare: true,
            },
          };
        });

        this.loaded = true;
        logger.debug("Loaded areas:", this.list.join(", "));
        return this.list;
      } catch (error) {
        logger.error("Error fetching areas:", error);
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getAreaData(areaId) {
      try {
        // Return cached data if available
        if (this.areasData[areaId]) {
          this.currentArea = areaId;
          return this.areasData[areaId];
        }

        logger.debug("Fetching area data:", areaId);
        const areaDoc = doc(db, "areas", areaId);
        const docSnap = await getDoc(areaDoc);

        if (docSnap.exists()) {
          const data = docSnap.data();
          this.areasData[areaId] = {
            ...data,
            id: areaId,
            availableMaps: data.availableMaps || {
              ageb: true,
              electoral: true,
              indices: true,
              compare: true,
            },
          };
          this.currentArea = areaId;
          return this.areasData[areaId];
        } else {
          logger.warn("Area not found:", areaId);
          return null;
        }
      } catch (error) {
        logger.error("Error fetching area data:", error);
        throw error;
      }
    },

    async getDefaultArea() {
      await this.fetchAreas();
      return this.list.length > 0 ? this.list[0] : null;
    },

    setCurrentArea(areaId) {
      this.currentArea = areaId;
    },

    /**
     * Update map availability for an area based on resource loading results
     * Called when resources fail to load (404) to disable menu items
     */
    updateMapAvailability(areaId, mapKey, available) {
      if (this.areasData[areaId]) {
        this.areasData[areaId].availableMaps[mapKey] = available;
        logger.debug(`Updated ${mapKey} availability for ${areaId}: ${available}`);
      }
    },

    /**
     * Create a new area in Firestore
     */
    async createArea(areaId, areaData) {
      try {
        const areaDoc = doc(db, "areas", areaId);
        const docData = {
          name: areaData.name,
          center: areaData.center,
          zoom: areaData.zoom,
          availableMaps: areaData.availableMaps || {
            ageb: true,
            electoral: true,
            indices: true,
            compare: true,
          },
          createdAt: new Date().toISOString(),
        };

        await setDoc(areaDoc, docData);

        // Update local cache
        this.list.push(areaId);
        this.areasData[areaId] = { id: areaId, ...docData };

        logger.debug("Area created:", areaId);
        return true;
      } catch (error) {
        logger.error("Error creating area:", error);
        throw error;
      }
    },

    /**
     * Delete an area from Firestore
     */
    async deleteArea(areaId) {
      try {
        await deleteDoc(doc(db, "areas", areaId));

        // Update local cache
        this.list = this.list.filter((id) => id !== areaId);
        delete this.areasData[areaId];

        if (this.currentArea === areaId) {
          this.currentArea = null;
        }

        logger.debug("Area deleted:", areaId);
        return true;
      } catch (error) {
        logger.error("Error deleting area:", error);
        throw error;
      }
    },

    clearCache() {
      this.list = [];
      this.areasData = {};
      this.currentArea = null;
      this.loaded = false;
      this.error = null;
    },
  },

  getters: {
    hasAreas: (state) => state.list.length > 0,
    areaCount: (state) => state.list.length,
    isLoading: (state) => state.loading,

    // Get available maps for current area
    currentAreaMaps: (state) => {
      if (!state.currentArea || !state.areasData[state.currentArea]) {
        return { ageb: true, electoral: true, indices: true, compare: true };
      }
      return state.areasData[state.currentArea].availableMaps;
    },

    // Get display name for current area
    currentAreaName: (state) => {
      if (!state.currentArea || !state.areasData[state.currentArea]) {
        return "";
      }
      return state.areasData[state.currentArea].name || state.currentArea;
    },
  },
});
